import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { assetGroupDefinition } from '../../../../entity-definitions/asset-group.definition';
import { EntityDialogService } from '../../../../services/entity-dialog.service';
import { EntityGeneratorConfig, EntityGeneratorService } from '../../../../services/EntityGeneratorService.service';
import { ToastService } from '../../../../services/toast.service';
import { AssetGroupStore } from '../../../../stores/asset-group.store';
import { AssetTypeStore } from '../../../../stores/asset-type.store';
import { AssetStore } from '../../../../stores/asset.store';
import { creationType, GenericStoreType } from '../../../../stores/generic.store';
import { AssetGroup } from '../../../../types/asset-group.interface';
import { AssetType } from '../../../../types/asset-type.interface';
import { Asset } from '../../../../types/asset.interface';
import { GenericEntity } from '../../../../types/generic-entity.interface';
import { GenericParentGridComponent } from '../../../generic/generic-parent-grid.component';
import { CesButtonComponent } from '../../../ui/ces-buttons';
import { CesTitleComponent } from '../../../ui/ces-route-title/ces-route-title.component';

type CreatableStore<T extends GenericEntity> = GenericStoreType<T> & {
  create(entity: creationType<T>): Promise<T | null>;
};

interface AssetGroupStoreApi {
  entities(): AssetGroup[];
  loadAll(): Promise<AssetGroup[]>;
  create(entity: AssetGroup): Promise<AssetGroup | null>;
  update(id: number, changes: Partial<AssetGroup>): Promise<AssetGroup | null>;
  delete(id: number): Promise<void>;
}

interface AssetStoreApi {
  entities(): Asset[];
  loadAll(): Promise<Asset[]>;
  create(entity: Partial<Asset>): Promise<Asset | null>;
  update(id: number, changes: Partial<Asset>): Promise<Asset | null>;
  delete(id: number): Promise<void>;
}

interface AssetTypeStoreApi {
  entities(): AssetType[];
  loadAll(): Promise<AssetType[]>;
}

@Component({
  selector: 'app-asset-groups-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CesTitleComponent, GenericParentGridComponent, CesButtonComponent, TranslocoModule],
  templateUrl: './asset-groups-list.component.html',
})
export class AssetGroupsListComponent implements OnInit {
  protected readonly assetGroupStore = inject(AssetGroupStore) as unknown as AssetGroupStoreApi;
  protected readonly assetStore = inject(AssetStore) as unknown as AssetStoreApi;
  protected readonly assetTypeStore = inject(AssetTypeStore) as unknown as AssetTypeStoreApi;
  private readonly dialogService = inject(EntityDialogService);
  private readonly entityGenerator = inject(EntityGeneratorService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translateService = inject(TranslocoService);
  private readonly toastService = inject(ToastService);

  dataLoadingCompleted = false;

  assetGroupDefinition = assetGroupDefinition;
  generatorConfig: EntityGeneratorConfig<AssetGroup> = {
    childParentIdField: 'assetGroupId',
    hasChildrenField: 'hasAsset',
    fieldMappings: {
      Asset: {
        typeIdField: 'assetTypeId',
        typeNameField: 'type',
      },
    },
  };

  assetGroups: AssetGroup[] = [];
  assetsByGroup: Record<number, Asset[]> = {};

  ngOnInit(): void {
    void this.loadData();
  }

  private async loadData(): Promise<void> {
    try {
      await this.assetGroupStore.loadAll();
      this.assetGroups = this.assetGroupStore.entities();

      await this.assetStore.loadAll();
      const assets = this.assetStore.entities();

      this.assetsByGroup = {};
      for (const group of this.assetGroups) {
        this.assetsByGroup[group.id] = assets.filter(asset => asset.assetGroupId === group.id);
      }

      await this.assetTypeStore.loadAll();
      this.dataLoadingCompleted = true;
    } catch (error) {
      console.error(this.translateService.translate('common.errors.dataLoadFailed'), error);
    } finally {
      this.cdr.markForCheck();
    }
  }

  createAssetGroup(): void {
    this.dialogService
      .openCreateDialog<AssetGroup>({
        header: this.assetGroupDefinition.messages.dialogTitles.create(),
        entityDefinition: this.assetGroupDefinition,
        mode: 'create',
      })
      .subscribe(formData => {
        if (!formData) return;
        if ('__action' in formData && formData.__action === 'delete') return;

        void this.assetGroupStore
          .create(formData as AssetGroup)
          .then(() => this.loadData())
          .catch((error: unknown) => {
            console.error(this.translateService.translate('common.errors.assetGroupCreationFailed'), error);
          });
      });
  }

  editAssetGroup(group: AssetGroup): void {
    this.dialogService
      .openEditDialog<AssetGroup>(group, {
        header: this.assetGroupDefinition.messages.dialogTitles.edit(),
        headerParams: { name: group.name },
        entityDefinition: this.assetGroupDefinition,
        mode: 'edit',
        onDelete: entity => this.deleteAssetGroup(entity),
      })
      .subscribe(result => {
        if (!result) return;
        if ('__action' in result && result.__action === 'delete') {
          this.deleteAssetGroup(result.entity as AssetGroup);
          return;
        }
        void this.assetGroupStore.update(group.id, result as Partial<AssetGroup>).then(() => this.loadData());
      });
  }

  deleteAssetGroup(group: AssetGroup): void {
    void this.assetGroupStore
      .delete(group.id)
      .then(() => this.loadData())
      .catch((error: unknown) => {
        this.toastService.warning('entities.assetGroup.messages.deleteError');
        console.error(this.translateService.translate('common.errors.assetGroupDeletionFailed'), error);
      });
  }

  createAsset(group: AssetGroup): void {
    if (!this.assetGroupDefinition.childDefinition) return;
    const assetTypes = this.assetTypeStore.entities();

    this.dialogService
      .openCreateDialog<Asset>({
        header: this.assetGroupDefinition.childDefinition.messages.dialogTitles.create(),
        entityDefinition: this.assetGroupDefinition.childDefinition,
        mode: 'create',
        parentId: group.id,
        additionalData: { AssetType: assetTypes },
      })
      .subscribe(result => {
        if (!result) return;
        if ('__action' in result && result.__action === 'delete') return;

        const assetData: Partial<Asset> = {
          ...(result as Partial<Asset>),
          assetGroupId: group.id,
        };

        void this.assetStore.create(assetData).then(() => {
          if (!group.hasAsset) {
            void this.assetGroupStore.update(group.id, { hasAsset: true });
          }
          void this.loadData();
        });
      });
  }

  editAsset(asset: Asset): void {
    if (!this.assetGroupDefinition.childDefinition) return;
    this.dialogService
      .openEditDialog<Asset>(asset, {
        header: this.assetGroupDefinition.childDefinition.messages.dialogTitles.edit(),
        headerParams: { name: asset.name },
        entityDefinition: this.assetGroupDefinition.childDefinition,
        mode: 'edit',
        additionalData: {
          AssetType: this.assetTypeStore.entities(),
        },
        onDelete: entity => this.deleteAsset(entity),
      })
      .subscribe(result => {
        if (!result) return;
        if ('__action' in result && result.__action === 'delete') {
          this.deleteAsset(result.entity as Asset);
          return;
        }
        void this.assetStore.update(asset.id, result as Partial<Asset>).then(() => {
          void this.loadData();
        });
      });
  }

  deleteAsset(asset: Asset): void {
    void this.assetStore
      .delete(asset.id)
      .then(() => {
        const group = this.assetGroups.find(g => g.id === asset.assetGroupId);
        if (group) {
          const groupAssets = this.assetsByGroup[group.id] ?? [];
          const remainingAssets = groupAssets.filter(a => a.id !== asset.id);
          if (remainingAssets.length === 0 && group.hasAsset) {
            void this.assetGroupStore.update(group.id, { hasAsset: false });
          }
        }
        void this.loadData();
      })
      .catch((error: unknown) => {
        this.toastService.warning('entities.asset.messages.deleteError');
        console.error(this.translateService.translate('common.errors.assetDeletionFailed'), error);
      });
  }

  async createRandomAssetGroup(): Promise<void> {
    try {
      const parent = await this.entityGenerator.generateRandomEntityWithChildren(
        'AssetGroup',
        'Asset',
        this.assetGroupStore as unknown as CreatableStore<AssetGroup>,
        this.assetStore as unknown as CreatableStore<Asset>,
        3,
        this.generatorConfig,
        this.assetTypeStore as unknown as GenericStoreType<GenericEntity>,
      );

      if (parent) {
        void this.loadData();
      }
    } catch (error) {
      console.error(this.translateService.translate('common.errors.testGroupCreationFailed'), error);
    }
  }

  async createRandomAsset(group: AssetGroup): Promise<void> {
    try {
      await this.entityGenerator.generateRandomChildEntities(
        group,
        'Asset',
        this.assetStore as unknown as CreatableStore<Asset>,
        1,
        this.generatorConfig,
        this.assetTypeStore as unknown as GenericStoreType<GenericEntity>,
      );

      if (!group.hasAsset) {
        await this.assetGroupStore.update(group.id, { hasAsset: true });
      }

      void this.loadData();
    } catch (error) {
      console.error(this.translateService.translate('common.errors.assetTestCreationFailed'), error);
    }
  }
}
